import React, { memo } from 'react';
import classNames from 'classnames';
import { Box, Link, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';

import Avatar from '../../../../../components/Avatar/Avatar';
import { formatPositionAtCompany } from '../../../helpers/recipient.helpers';
import { TFullSender } from '../../../../../store/users/users.types';

import FunFacts from './FunFacts';

const useStyles = makeStyles(({ spacing, palette }) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: 'auto',
    position: 'relative',
    width: 'auto',
    maxWidth: 500,
    backgroundColor: palette.common.white,
    transition: 'height .2s',
    padding: spacing(4),
    borderRadius: 4,
    direction: 'ltr',
    boxShadow: '0 2px 10px 0 rgba(0,0,0,.2), 0 2px 2px 0 rgba(0,0,0,.2)',
    '@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none)': {
      width: '100%',
    },
  },
  avatar: {
    width: 90,
    height: 90,
    marginBottom: spacing(2),
  },
  text: {
    wordBreak: 'break-word',
    wordWrap: 'break-word',
    textAlign: 'center',
  },
}));

export interface ISenderInfoDesktopProps {
  sender: TFullSender;
  onEmailClick: () => void;
  onPhoneClick: () => void;
}

const SenderInfoDesktop = ({ sender, onEmailClick, onPhoneClick }: ISenderInfoDesktopProps) => {
  const classes = useStyles({ sender });

  const jobDesc = formatPositionAtCompany(sender);

  return (
    <Box className={classes.root}>
      <Avatar className={classes.avatar} src={sender.image} alt="Sender avatar" />
      <Box width={1} className={classNames('H3-Dark', classes.text)} data-testid="giftSenderName">
        {sender.fullName}
      </Box>
      <Box mb={0.5} textAlign="left" data-testid="giftSenderPosition">
        <Typography className={classNames('Body-Regular-Center-Static-Bold', classes.text)}>{jobDesc}</Typography>
      </Box>
      <Box mb={1} data-testid="giftSenderEmail">
        <Link onClick={onEmailClick} href={`mailto:${sender.email}`} className={classes.text} underline="hover">
          {sender.email}
        </Link>
      </Box>
      {sender.phone && (
        <Box textAlign="left" fontSize={16} fontFamily="Lato" data-testid="giftSenderPhone">
          <Link onClick={onPhoneClick} href={`tel:${sender.phone}`} className={classes.text} underline="hover">
            {sender.phone}
          </Link>
        </Box>
      )}
      {sender.funFacts && <FunFacts facts={sender.funFacts} />}
    </Box>
  );
};

export default memo(SenderInfoDesktop);
